<template>
  <ModalComponent
    :model-value="showRef"
    :max-width="DEFAULT_MAX_WIDTH_MODAL"
    :visible="showRef"
    :close="handleCloseModal"
    :style-class-contain="styleClassContain"
    @close="handleCloseModal"
  >
    <ModalHeader
      :title="
        isVerifiedEmail
          ? getUiText('app-profile-modal-update-email-title-1')
          : getUiText('app-settings-domain-add-modal-title-1')
      "
      style="z-index: 10"
      @close="handleCloseModal"
    />
    <div class="tw-p-10 modal-verify-domain">
      <span
        v-if="!isVerifiedEmail"
        class="tw-text-[25px] tw-text-[#211F58] tw-font-[600] tw-leading-none tw-capitalize"
        >{{
          getUiText("app-settings-domain-verify-modal-description-title-1")
        }}</span
      >
      <p
        v-if="!isVerifiedEmail"
        class="tw-leading-[130%] tw-text-[15px] tw-pt-[10px] tw-mb-0"
      >
        <span class="tw-mr-[2px]">{{
          getUiText("app-settings-domain-verify-modal-description-A-1")
        }}</span>
        <span><VRuntimeTemplate :template="getVerifyDynamicText()" /> </span>
      </p>
      <p v-if="!isVerifiedEmail" class="tw-leading-none tw-pt-[10px] tw-mb-0">
        <span class="tw-text-[15px]">{{
          getUiText("app-settings-domain-verify-modal-description-C-1")
        }}</span>
      </p>
      <div
        class="tw-p-[40px] tw-bg-[#F7F7F8] tw-rounded tw-border tw-border-solid tw-border-[#EFF0F1]"
        :class="{ 'tw-mt-[40px]': !isVerifiedEmail }"
      >
        <span class="tw-text-[16px] tw-font-[600] tw-text-[#211F58]">{{
          isVerifiedEmail
            ? getUiText("app-profile-modal-update-email-address-secret-title-1")
            : getUiText("app-settings-domain-verify-modal-dns-text-1")
        }}</span>
        <p class="tw-mt-[10px] tw-text-[15px] tw-mb-0 tw-text-[#231F20]">
          <span>
            <VRuntimeTemplate
              :template="
                getProcessedUIText(
                  isVerifiedEmail
                    ? subTitle
                    : 'app-settings-domain-verify-modal-dns-description-1',
                  isVerifiedEmail ? dynamicEmailTags : dynamicDescriptionTags,
                )
              "
            />
          </span>
        </p>
        <CopyInfo
          class="copy-info-container tw-mt-[25px] tw-pl-[20px] tw-pr-[14px] tw-py-[10px] tw-bg-[#FFFFFF] tw-rounded-md"
          :text="isVerifiedEmail ? secretVerifyCode : domainVerifyCode"
          :style-icon-css="'tw-w-[18px] tw-h-[18px]'"
        />
      </div>
      <BaseForm :enter-event="true" :show="showRef" @on:submit="verifyDomain">
        <div class="button-container tw-flex tw-justify-end tw-mt-[40px]">
          <div v-if="!isVerifiedEmail" class="button-container-left tw-flex-1">
            <b-button
              variant="danger"
              class="button-delete tw-bg-[#F7553C] tw-py-[11px] tw-px-[18px] tw-text-[15px] tw-font-medium tw-rounded-[27px]"
              @click="() => deleteDomain()"
            >
              {{
                getUiText("app-settings-domain-verify-modal-delete-button-1")
              }}
            </b-button>
          </div>
          <div class="button-container-right">
            <b-button
              v-if="!isVerifiedEmail"
              variant="primary"
              data-cy="verify-add-domain-later-button"
              class="button-verify-later tw-bg-[#FFFFFF] tw-py-[11px] tw-px-[18px] tw-text-[15px] tw-font-medium tw-mr-[14px] tw-rounded-[27px]"
              @click="() => handleCloseModal()"
            >
              {{
                getUiText(
                  "app-settings-domain-verify-modal-verify-later-button-1",
                )
              }}
            </b-button>
            <b-button
              variant="primary"
              data-cy="verify-add-domain-submit-button"
              :class="`button-setting-add-domain ${
                isLoadingVerifiedEmail ? 'tw-relative' : ''
              } button-verify tw-bg-[#387DFF] tw-py-[11px] tw-px-[18px] tw-text-[15px] tw-font-medium tw-rounded-[27px]`"
              type="submit"
            >
              <span
                :class="`${
                  isLoadingVerifiedEmail ? 'tw-text-transparent' : ''
                }`"
                >{{
                  isVerifiedEmail
                    ? getUiText(
                        "app-profile-modal-update-email-address-button-1",
                      )
                    : getUiText(
                        "app-settings-domain-verify-modal-verify-button-1",
                      )
                }}</span
              >
              <div
                :class="`tw-w-[39px] tw-h-[39px] tw-flex tw-items-center tw-justify-center tw-absolute tw-top-[-1px] ${
                  isLoadingVerifiedEmail ? '' : 'tw-hidden'
                }`"
              >
                <!-- eslint-disable vue/no-unused-refs -->
                <LottieAnimation
                  ref="anim"
                  :animation-data="ProgressAnimation"
                  :auto-play="true"
                  :loop="true"
                />
              </div>
            </b-button>
          </div>
        </div>
      </BaseForm>
    </div>
  </ModalComponent>
</template>

<script setup lang="ts">
import store from "../../store";
import utilities, { getComponentUiText } from "../../utils/utilities";
import { PropType, Ref, computed, onMounted, ref, watch } from "vue";

import ModalComponent from "@/components/common/modalComponent.vue";
import ModalHeader from "../common/modalHeader.vue";
import CopyInfo from "@/components/common/copyInfo.vue";
import ProgressAnimation from "@/assets/animation/progress_dark.json";
import { LottieAnimation } from "lottie-web-vue";
import VRuntimeTemplate from "vue3-runtime-template";
import {
  postOrgDomainVerifyInitiate,
  postOrgDomainVerify,
} from "@/services/org";
import get from "lodash/get";
import { IDomain } from "@/interfaces/org/domain";
import { IToastMessage, ToastVariant } from "@/interfaces/common/IToastMessage";
import EventEmitter, { EmitterEvents } from "@/utils/eventEmitter";
import commonServices from "@/services/common/commonServices";
import BaseForm from "../common/form/baseForm.vue";
import { showErrorAPI } from "@/services/apiRoutes";
import { Org } from "@/services/apiRoutes/routes";

const DEFAULT_MAX_WIDTH_MODAL = 65;

const props = defineProps({
  show: {
    type: Boolean,
    required: false,
  },
  domain: {
    type: Object as PropType<IDomain>,
    required: false,
    default: () => ({}) as IDomain,
  },
  isVerifiedEmail: {
    type: Boolean,
    required: false,
  },
  subTitle: {
    type: String,
    required: false,
    default: "",
  },
  secret: {
    type: String,
    required: true,
  },
});
const emit = defineEmits([
  "update:show",
  "update:domain",
  "delete:domain",
  "update:close",
]);
const domainVerifyCode: Ref<string> = ref("");
const errorMessageVerifyDomain: Ref<string> = ref("");
const commonService = new commonServices();
const isLoadingVerifiedEmail = ref(false);
const secretVerifyCode: Ref<string> = ref(props.secret);
let showRef: Ref<boolean> = ref(props.show);

const dynamicDescriptionTags: any = {
  "app-settings-domain-verify-modal-verify-button-1": {
    openTag: "<span class='tw-font-semibold'>‘",
    closeTag: "’</span>",
  },
};
const dynamicEmailTags: any = {
  "app-profile-modal-update-email-address-button-1": {
    openTag: "<span class='tw-font-semibold'>‘",
    closeTag: "’</span>",
  },
};

watch(
  () => props.show,
  (value) => {
    showRef.value = value;
    if (value) initial();
  },
);

onMounted(() => {
  if (props.show) {
    initial();
  }
});

watch(
  () => props.secret,
  (value) => {
    secretVerifyCode.value = value;
  },
);
const initial = () => {
  if (!props.isVerifiedEmail) fetchDomainVerifyCode();
};

const defaultUiText = computed(() => store.state.LanguagePicker.defaultUiText);
const languageSelectedText = computed(
  () => store.state.LanguagePicker.languageSelectedText,
);

const getUiText = (id: string, isArray = false) => {
  return utilities.getUiText(
    id,
    languageSelectedText.value,
    defaultUiText.value,
    isArray,
  );
};

const handleCloseModal = (isCancel = false) => {
  isLoadingVerifiedEmail.value = false;
  store.commit("User/setIsCloseModalProfile", true);
  showRef.value = false;
  emit("update:show", showRef.value);
  emit("update:close", isCancel);
};

const fetchDomainVerifyCode = async () => {
  try {
    const res = await postOrgDomainVerifyInitiate(
      parseInt(props.domain.domainId) || 0,
    );
    domainVerifyCode.value = get(res, "data.data.verificationToken", "");
  } catch (error: any) {
    domainVerifyCode.value = "";
    if (error && error.data) {
      showErrorAPI(error, Org.orgDomainVerifyInitiate);
    }
  }
};

const verifyDomain = async () => {
  if (props.isVerifiedEmail) {
    handleCloseModal();
    return;
  }
  commonService.setCursorLoading();
  const domainUpdate = await verifyAndUpdateDomainData();
  commonService.setCursorDefault();

  if (!domainUpdate?.isVerified && errorMessageVerifyDomain.value !== "") {
    const emitData: IToastMessage = {
      variant: ToastVariant.ERROR,
      message: errorMessageVerifyDomain.value,
    };
    EventEmitter.emit(EmitterEvents.APP_LAYOUT_SHOW_TOAST_MESSAGE, emitData);
    return;
  }

  if (domainUpdate?.isVerified) {
    emit("update:domain", domainUpdate);
    showToastSuccessVerify();
    handleCloseModal();
    return;
  }
};

const deleteDomain = async () => {
  emit("delete:domain", props.domain.domainId);
  handleCloseModal();
};

const verifyAndUpdateDomainData = async () => {
  try {
    isLoadingVerifiedEmail.value = true;
    const res = await postOrgDomainVerify(parseInt(props.domain.domainId) || 0);
    const isVerified = get(res, "data.data.isVerified", false);
    errorMessageVerifyDomain.value = get(res, "data.message", "");

    return { ...props.domain, isVerified };
  } catch (error: any) {
    if (error && error.data) {
      showErrorAPI(error, Org.getMembersOfRole);
      return { ...props.domain, isVerified: false };
    }
  } finally {
    isLoadingVerifiedEmail.value = false;
  }
};

const showToastSuccessVerify = () => {
  const message = getComponentUiText(
    "app-settings-domain-verify-success-message-1",
    false,
    { domain: props.domain.domain },
  );
  const emitData: IToastMessage = { variant: ToastVariant.SUCCESS, message };
  EventEmitter.emit(EmitterEvents.APP_LAYOUT_SHOW_TOAST_MESSAGE, emitData);
};

const getVerifyDynamicText = (): string => {
  const dynamicDomain = `<span class='tw-font-semibold'>${props.domain.domain}</span>`;
  return getUiText("app-settings-domain-verify-modal-description-B-1")?.replace(
    "{##domainName##}",
    dynamicDomain,
  );
};

const getProcessedUIText = (id: string, tags: any) => {
  return utilities.getProcessedUIText(
    id,
    tags,
    languageSelectedText.value,
    defaultUiText.value,
  );
};

const styleClassContain = {
  "border-radius": "4px",
  "box-shadow": "0px 0px 4px 0px rgba(0, 0, 0, 0.05)",
};
</script>

<style lang="scss">
.copy-info-container {
  max-width: 70%;
}

.text-copy-container {
  width: calc(100% - 22px);
}

.button-delete {
  line-height: 100%;
  background-color: #f7553c;
  border-color: #f7553c;
}

.button-verify-later {
  line-height: 100%;
  border: 2px solid #387dff;
  background-color: #ffffff;
  color: #387dff !important;
}

.button-verify {
  line-height: 100%;
  background-color: #387dff;
  border-color: #387dff;
}

@media (max-width: 991px) {
  .copy-info-container {
    max-width: fit-content;
  }
}

@media (max-width: 420px) {
  .modal-verify-domain {
    .button-container {
      flex-direction: column-reverse;
    }

    .button-container-left {
      margin-top: 10px;
    }
  }
}

@media (max-width: 768px) {
  .modal-verify-domain {
    padding: 10px;
  }
}
</style>
